import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, InputAdornment, Paper, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    MenuItem, useTheme, Divider, Menu, Checkbox, FormControlLabel, IconButton,
    Chip, ListItemIcon, ListItemText, Tooltip, CircularProgress, FormGroup
} from '@mui/material';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { gql, useMutation } from '@apollo/client';
import {
    FiberManualRecord as FiberManualRecordIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    MoreHoriz as MoreHorizIcon,
    InfoOutlined as InfoOutlinedIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import SuccessToast from '../../SuccessToast';
import BudgetDetailsPanel from './BudgetDetailsPanel';
import BudgetEmptyState from './BudgetEmptyState';

// --- GraphQL Mutations ---
const DELETE_BUDGET = gql`
    mutation DeleteBudget($id: ID!) {
        deleteBudget(id: $id)
    }
`;

// --- Helper functions ---
const formatValue = (value: number, metric?: 'COST' | 'TOKENS' | string) => {
    if (typeof value !== 'number') return value;
    const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);

    if (metric === 'TOKENS') {
        return `${formattedNumber} T`;
    }
    
    // Default to COST format
    return `$${formattedNumber}`;
};

const getProviderIcon = (providerType: string): string => {
  switch (providerType) {
    case 'GCP': return '/icons/google-cloud-icon-2048x1646-7admxejz.svg';
    case 'AWS': return '/icons/aws.svg';
    case 'Azure': return '/icons/azure2.svg';
    default: return '';
  }
};

// --- Interfaces ---
interface Creator { id?: string; name: string | null; photo?: string | null; }
interface ScopeConfiguration {
    id:string;
    dataSources: { id: string, name: string }[];
    regions: string[] | null;
    services: string[] | null;
    labels: { [key: string]: string; } | null;
    k8sClusters: string[] | null;
    k8sNamespaces: string[] | null;
}
interface Budget {
    id: string;
    name: string;
    description: string;
    provider: string;
    period: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
    status: 'ON_TRACK' | 'THRESHOLD_BREACHED' | 'BUDGET_BREACHED';
    spend: number;
    budgetLimit: number;
    forecasted: number;
    credits: number;
    addedBy: Creator;
    thresholds: number[];
    includeCredits: boolean;
    tags?: { [key: string]: string; } | null | undefined;
    scopeConfigurations: ScopeConfiguration[];
    userIdsToNotify: string[];
    metric: 'COST' | 'TOKENS';
}
interface Tenant { id: string; name: string; }
interface User { id: string; name?: string | null; photo?: string | null; }

interface BudgetContextType {
  currentTenant: Tenant | null;
  userRole?: string | null;
  setSidebarOpen?: (isOpen: boolean) => void;
  data: { budgetsByTenant: any[] };
  loading: boolean;
  error: any;
  refetch: () => void;
}

// --- Helper function to get specific threshold message ---
const getBreachedThresholdMessage = (spend: number, budgetLimit: number, thresholds: number[]): string => {
    if (budgetLimit <= 0 || !thresholds || thresholds.length === 0) {
        return 'A spending threshold has been breached';
    }
    const percentage = (spend / budgetLimit) * 100;
    const sortedThresholds = [...thresholds].sort((a, b) => b - a);
    for (const threshold of sortedThresholds) {
        if (percentage >= threshold) {
            return `${threshold}% threshold breached`;
        }
    }
    return 'A spending threshold has been breached';
};

// --- Main status mapping function now generates specific messages ---
const mapApiStatusToDisplay = (budget: Budget) => {
    const { status, spend, budgetLimit, thresholds } = budget;
    switch (status) {
        case 'ON_TRACK':
            return { status: 'On Track', color: '#03A65A', message: '' };
        case 'THRESHOLD_BREACHED':
             return {
                 status: 'Threshold Breached',
                 color: '#D4A72C',
                 message: getBreachedThresholdMessage(spend, budgetLimit, thresholds)
             };
        case 'BUDGET_BREACHED':
            return { status: 'Budget Breached', color: '#CA2B20', message: 'Spend exceeds budget limit' };
        default:
            return { status: 'On Track', color: '#03A65A', message: '' };
    }
};

const getStatusChipSx = (status: string): any => {
    switch (status) {
        case 'On Track': return { backgroundColor: 'rgba(3, 166, 90, 0.1)', color: 'rgba(3, 166, 90, 1)', fontWeight: 500, pointerEvents: 'none', border: '1px solid rgba(3, 166, 90, 1)' };
        case 'Threshold Breached': return { backgroundColor: 'rgba(255, 193, 7, 0.1)', color: 'rgba(212, 167, 44, 1)', fontWeight: 500, pointerEvents: 'none', border: '1px solid rgba(212, 167, 44, 1)' };
        case 'Budget Breached': return { backgroundColor: 'rgb(240, 213, 211)', color: 'rgba(202, 43, 32, 1)', fontWeight: 500, pointerEvents: 'none', border: '1px solid rgba(202, 43, 32, 1)' };
        default: return { backgroundColor: 'grey.200', color: 'grey.800', fontWeight: 500, border: '1px solid grey.400' };
    }
};

const providerOptions = ['GCP', 'AWS', 'Azure'];
const periodOptions = ['Monthly', 'Quarterly', 'Yearly'];
const statusOptions = ['On Track', 'Threshold Breached', 'Budget Breached'];

// --- 1. Define the Error State Component in the same file ---
interface DataErrorStateProps {
    onRefresh: () => void;
}

const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                height: '100%',
                minHeight: '400px', 
                p: 3,
                gap: 1,
            }}
        >
            <img 
                src="/icons/unable_to_diplay_data.svg" 
                alt="Hourglass indicating a temporary issue" 
                style={{ width: '120px', height: '120px' }} 
            />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
                Unable to Display Data
            </Typography>
            <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
                We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
            </Typography>
            <Button
                variant="text"
                onClick={onRefresh}
                sx={{
                    color: '#0064FF',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    marginTop: '2px',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 100, 255, 0.04)'
                    }
                }}
            >
                Refresh Page
            </Button>
        </Box>
    );
};


// --- Main Page Component ---
const TotalBudgetsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { 
        currentTenant, 
        userRole, 
        setSidebarOpen, 
        data, 
        loading, 
        error, 
        refetch 
    } = useOutletContext<BudgetContextType>();

    const [deleteBudget, { loading: deleteLoading }] = useMutation(DELETE_BUDGET);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedBudgetForPanel, setSelectedBudgetForPanel] = useState<Budget | null>(null);
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
    const isActionMenuOpen = Boolean(actionMenuAnchorEl);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [providerFilters, setProviderFilters] = useState<string[]>(providerOptions);
    const [tempProviderFilters, setTempProviderFilters] = useState<string[]>(providerOptions);
    const [providerMenuAnchorEl, setProviderMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isProviderMenuOpen = Boolean(providerMenuAnchorEl);
    const [periodFilter, setPeriodFilter] = useState<string[]>(periodOptions);
    const [tempPeriodFilter, setTempPeriodFilter] = useState<string[]>(periodOptions);
    const [periodMenuAnchorEl, setPeriodMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isPeriodMenuOpen = Boolean(periodMenuAnchorEl);
    const [statusFilter, setStatusFilter] = useState<string[]>(statusOptions);
    const [tempStatusFilter, setTempStatusFilter] = useState<string[]>(statusOptions);
    const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isStatusMenuOpen = Boolean(statusMenuAnchorEl);


    const budgets: Budget[] = useMemo(() => {
        if (!data?.budgetsByTenant) return [];
        return data.budgetsByTenant.map((b: any): Budget => ({
            id: b.id,
            name: b.name,
            description: b.description || '',
            provider: b.provider,
            period: b.period,
            status: b.status,
            spend: b.currentSpend,
            budgetLimit: b.amount,
            forecasted: b.forecastedSpend,
            credits: b.currentCredits,
            addedBy: { name: b.creator?.name || 'N/A', photo: b.creator?.photo || undefined },
            thresholds: b.alertThresholds,
            includeCredits: b.includeCredits,
            tags: b.tags,
            userIdsToNotify: b.userIdsToNotify || [],
            metric: b.metric || 'COST',
            scopeConfigurations: b.scopeConfigurations.map((sc: any) => ({
                id: sc.id,
                dataSources: sc.dataSources,
                regions: sc.regions,
                services: sc.services,
                labels: sc.labels
                    ? sc.labels.reduce((acc: { [key: string]: string }, label: { key: string; value: string }) => {
                          acc[label.key] = label.value;
                          return acc;
                      }, {})
                    : null,
                k8sClusters: sc.k8sClusters,
                k8sNamespaces: sc.k8sNamespaces,
            })),
        }));
    }, [data]);

     useEffect(() => {
        const state = location.state as { budgetAdded?: boolean; budgetUpdated?: boolean; budgetName?: string; };
        if (state?.budgetAdded) { setToastMessage(`Budget "${state.budgetName || 'new budget'}" added successfully`); setToastOpen(true); refetch(); navigate(location.pathname, { replace: true, state: {} }); }
        else if (state?.budgetUpdated) { setToastMessage(`Budget "${state.budgetName || 'budget'}" updated successfully`); setToastOpen(true); refetch(); navigate(location.pathname, { replace: true, state: {} }); }
    }, [location, navigate, refetch]);
    const onTrackCount = useMemo(() => budgets.filter(b => b.status === 'ON_TRACK').length, [budgets]);
    const thresholdBreachedCount = useMemo(() => budgets.filter(b => b.status === 'THRESHOLD_BREACHED').length, [budgets]);
    const budgetBreachedCount = useMemo(() => budgets.filter(b => b.status === 'BUDGET_BREACHED').length, [budgets]);
    const filteredData = useMemo(() => {
        return budgets.filter(budget => {
            const displayStatus = mapApiStatusToDisplay(budget).status;
            const matchesSearch = budget.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesProvider = providerFilters.length === providerOptions.length || providerFilters.includes(budget.provider);
            const matchesPeriod = periodFilter.length === periodOptions.length || periodFilter.map(p => p.toUpperCase()).includes(budget.period);
            const matchesStatus = statusFilter.length === statusOptions.length || statusFilter.includes(displayStatus);
            return matchesSearch && matchesProvider && matchesPeriod && matchesStatus;
        });
    }, [budgets, searchTerm, providerFilters, periodFilter, statusFilter]);
    const handleRowClick = (budget: Budget) => { if (typeof setSidebarOpen === 'function') { setSidebarOpen(false); } setSelectedBudgetForPanel(budget); setIsPanelOpen(true); };
    const handlePanelClose = () => { if (typeof setSidebarOpen === 'function') { setSidebarOpen(true); } setIsPanelOpen(false); setSelectedBudgetForPanel(null); };
    const handleAddBudgetClick = () => navigate('/budget-alerts/add', { state: { tenantId: currentTenant?.id } });
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, budget: Budget) => { event.stopPropagation(); setActionMenuAnchorEl(event.currentTarget); setSelectedBudget(budget); };
    const handleMenuClose = () => { setActionMenuAnchorEl(null); setSelectedBudget(null); };
    const handleEdit = () => {
        const budgetToEdit = selectedBudget;
        handleMenuClose();
        if (budgetToEdit && budgetToEdit.id) {
            navigate(`/budget-alerts/edit/${budgetToEdit.id}`);
        } else {
            console.error("Edit failed: Budget data is incomplete or ID is missing.", budgetToEdit);
            setToastMessage("Could not edit budget. The budget ID is missing.");
            setToastOpen(true);
        }
    };
    
    const handleDelete = async () => {
    const budgetToDelete = selectedBudget;
    handleMenuClose();

    if (!budgetToDelete) {
      return; 
    }

    try {
        await deleteBudget({ variables: { id: budgetToDelete.id } });
        setToastMessage(`Budget "${budgetToDelete.name}" deleted successfully.`);
        setToastOpen(true);
        refetch();
    }
    catch (err: any) {
        // Log the technical error for developers
        console.error("Error deleting budget:", err);
        
        // Show the user-friendly message
        setToastMessage("We are unable to delete the budget at this moment. Please try again after some time.");
        setToastOpen(true);
    }
};

    const handleProviderMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setTempProviderFilters(providerFilters);
      setProviderMenuAnchorEl(event.currentTarget);
    };
    const handleProviderMenuClose = () => {
      setProviderMenuAnchorEl(null);
    };
    const handleApplyProvider = () => {
      setProviderFilters(tempProviderFilters);
      handleProviderMenuClose();
    };
    const handleClearProvider = () => {
      setTempProviderFilters([]);
      setProviderFilters([]);
      handleProviderMenuClose();
    };
    const handleProviderCheckboxChange = (provider: string) => {
      setTempProviderFilters(prev =>
          prev.includes(provider)
              ? prev.filter(p => p !== provider)
              : [...prev, provider]
      );
    };
    const handleSelectAllProviders = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempProviderFilters(event.target.checked ? providerOptions : []);
    };
    const getProviderButtonText = () => {
      const count = providerFilters.length;
      if (count === providerOptions.length) return 'Cloud Provider';
      return `${count} Providers`;
    };
    const handlePeriodMenuOpen = (e: React.MouseEvent<HTMLElement>) => { setTempPeriodFilter([...periodFilter]); setPeriodMenuAnchorEl(e.currentTarget); };
    const handlePeriodMenuClose = () => setPeriodMenuAnchorEl(null);
    const handleApplyPeriod = () => { setPeriodFilter(tempPeriodFilter); handlePeriodMenuClose(); };
    const handleTempPeriodChange = (period: string) => { const newSelection = tempPeriodFilter.includes(period) ? tempPeriodFilter.filter(p => p !== period) : [...tempPeriodFilter, period]; setTempPeriodFilter(newSelection); };
    const handleSelectAllPeriods = (event: React.ChangeEvent<HTMLInputElement>) => { setTempPeriodFilter(event.target.checked ? periodOptions : []); };
    const handleClearPeriod = () => {
        setTempPeriodFilter([]);
        setPeriodFilter([]);
        handlePeriodMenuClose();
    };
    const getPeriodButtonText = () => {
        const count = periodFilter.length;
        if (count === periodOptions.length) return 'Period';
        return `${count} Periods`;
    };
    const handleStatusMenuOpen = (e: React.MouseEvent<HTMLElement>) => { setTempStatusFilter([...statusFilter]); setStatusMenuAnchorEl(e.currentTarget); };
    const handleStatusMenuClose = () => setStatusMenuAnchorEl(null);
    const handleApplyStatus = () => { setStatusFilter(tempStatusFilter); handleStatusMenuClose(); };
    const handleTempStatusChange = (status: string) => { const newSelection = tempStatusFilter.includes(status) ? tempStatusFilter.filter(s => s !== status) : [...tempStatusFilter, status]; setTempStatusFilter(newSelection); };
    const handleSelectAllStatuses = (event: React.ChangeEvent<HTMLInputElement>) => { setTempStatusFilter(event.target.checked ? statusOptions : []); };
    const handleClearStatus = () => {
        setTempStatusFilter([]);
        setStatusFilter([]);
        handleStatusMenuClose();
    };
    const getStatusButtonText = () => {
        const count = statusFilter.length;
        if (count === statusOptions.length) return 'Status';
        return `${count} Statuses`;
    };
        
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', boxSizing: 'border-box' }}>
            <SuccessToast open={toastOpen} message={toastMessage} onClose={() => setToastOpen(false)} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', flexShrink: 0 }}>
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                     <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 600, mb: -1 }}>Total Budgets ({!loading && !error ? budgets.length : 0})</Typography>
                     <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 0, mt: -0.4 }}> Prevent overspending by reviewing and editing your cloud budgets at a glance. </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                         <Chip icon={<FiberManualRecordIcon sx={{ fill: theme.palette.success.main, width: '10px', height: '10px' }} />} label={`On Track ${onTrackCount}`} size="small" sx={{ gap: '4px', backgroundColor: 'rgba(232, 245, 233, 1)', color: 'rgba(102, 102, 102, 1)', fontWeight: 400, borderRadius: '14px', padding: '0px 8px', pointerEvents: 'none' }} />
                         <Chip icon={<FiberManualRecordIcon sx={{ fill: 'rgba(212, 167, 44, 1)', width: '10px', height: '10px' }} />} label={`Threshold Breached ${thresholdBreachedCount}`} size="small" sx={{ gap: '4px', backgroundColor: 'rgba(255, 193, 7, 0.1)', color: 'rgba(102, 102, 102, 1)', fontWeight: 400, borderRadius: '14px', padding: '0px 8px', pointerEvents: 'none' }} />
                         <Chip icon={<FiberManualRecordIcon sx={{ fill: '#CA2B20', width: '10px', height: '10px' }} />} label={`Budget Breached ${budgetBreachedCount}`} size="small" sx={{ gap: '4px', backgroundColor: 'rgba(202, 43, 32, 0.1)', color: 'rgba(102, 102, 102, 1)', fontWeight: 400, borderRadius: '14px', padding: '0px 8px', pointerEvents: 'none' }} />
                     </Box>
                 </Box>
                 {userRole !== 'viewer' && (
                     <Button onClick={handleAddBudgetClick} sx={{ backgroundColor: '#0064FF', width: '145px', height: '36px', textTransform: 'none', fontSize: '14px', fontWeight: 500, borderRadius: '8px', color: ' #FFFFFF' }}>Add New Budget</Button>
                 )}
            </Box>

            <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', flexShrink: 0 }}>
                 <TextField variant="outlined" size="small" placeholder="Search by budget name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ minWidth: { xs: '10px', sm: '250px' }, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'var(--black-4, rgba(0, 0, 0, 0.04))', color: '#999999', fontWeight: '300', '& fieldset': { border: 0 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight: 300, fontSize: '14px', color: '#999999' } }}
                 InputProps={{ startAdornment: (<InputAdornment position="start"> <img src="/icons/search_icon.svg" alt="Search" style={{ width: '17px', height: '17px' }} /> </InputAdornment>) }} />
                <Button
                    id="provider-filter-button" onClick={handleProviderMenuOpen} endIcon={<KeyboardArrowDownIcon />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isProviderMenuOpen || providerFilters.length < providerOptions.length ? '#0064FF' : theme.palette.divider, backgroundColor: isProviderMenuOpen || providerFilters.length < providerOptions.length ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }, }}>
                   <Typography component="span" sx={{ fontSize: '14px' }}>{getProviderButtonText()}</Typography>
                </Button>
                <Menu
                    id="provider-filter-menu" anchorEl={providerMenuAnchorEl} open={isProviderMenuOpen} onClose={handleProviderMenuClose} MenuListProps={{ 'aria-labelledby': 'provider-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}>
                    <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Provider</Typography></Box>
                    <Divider />
                    <Box sx={{ py: '10px' }}>
                        <FormControlLabel label="All" sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempProviderFilters.length === providerOptions.length && { backgroundColor: '#F3F8FE' }) }} control={ <Checkbox size="small" checked={tempProviderFilters.length === providerOptions.length} indeterminate={tempProviderFilters.length > 0 && tempProviderFilters.length < providerOptions.length} onChange={handleSelectAllProviders} sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } />
                        <Divider sx={{ borderColor: 'white', my: 0.1 }} />                        
                        <FormGroup>
                            {providerOptions.map((provider, index) => (
                                <React.Fragment key={provider}>
                                    <FormControlLabel control={ <Checkbox checked={tempProviderFilters.includes(provider)} onChange={() => handleProviderCheckboxChange(provider)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><img src={getProviderIcon(provider)} alt={provider} style={{ height: 20, width: 20, marginRight: 8 }} />{provider}</Box> } sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempProviderFilters.includes(provider) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempProviderFilters.includes(provider) ? '#333333' : '#666666', fontSize: '14px' } } }} />
                                    {index < providerOptions.length - 1 && <Divider sx={{ borderColor: 'white', my: 0.1 }} />}
                                </React.Fragment>
                            ))}
                        </FormGroup>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}>
                        <Button onClick={handleClearProvider} sx={{ textTransform: 'none', fontSize:'14px', color: tempProviderFilters.length > 0 ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button>
                        <Button onClick={handleApplyProvider} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button>
                    </Box>
                </Menu>
                <Button id="period-filter-button" onClick={handlePeriodMenuOpen} endIcon={<KeyboardArrowDownIcon />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isPeriodMenuOpen || periodFilter.length < periodOptions.length ? '#0064FF' : theme.palette.divider, backgroundColor: isPeriodMenuOpen || periodFilter.length < periodOptions.length ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' } }}>
                    <Typography component="span" sx={{ fontSize: '14px' }}>{getPeriodButtonText()}</Typography>
                </Button>
                <Menu anchorEl={periodMenuAnchorEl} open={isPeriodMenuOpen} onClose={handlePeriodMenuClose} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}>
                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontSize: '14px' }}>Select Period</Typography></Box>
                    <Divider />
                    <Box sx={{ py: '8px' }}>
                        <FormControlLabel label="All" sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempPeriodFilter.length === periodOptions.length && { backgroundColor: '#F3F8FE' }) }} control={<Checkbox size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} checked={tempPeriodFilter.length === periodOptions.length} indeterminate={tempPeriodFilter.length > 0 && tempPeriodFilter.length < periodOptions.length} onChange={handleSelectAllPeriods} />} />
                        <Divider sx={{ borderColor: 'white', my: 0.1 }} /> 
                        {periodOptions.map((p, index) => (
                           <React.Fragment key={p}>
                                <FormControlLabel value={p} control={<Checkbox checked={tempPeriodFilter.includes(p)} onChange={() => handleTempPeriodChange(p)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={p} sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempPeriodFilter.includes(p) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempPeriodFilter.includes(p) ? '#333333' : '#666666', fontSize: '14px' } } }}/>
                                {index < providerOptions.length - 1 && <Divider sx={{ borderColor: 'white', my: 0.1 }} />}
                           </React.Fragment>
                        ))}
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '2px' }}>
                        <Button onClick={handleClearPeriod} sx={{ textTransform: 'none', fontSize:'14px', color: tempPeriodFilter.length > 0 ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button>
                        <Button onClick={handleApplyPeriod} sx={{ textTransform: 'none', fontSize: '14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button>
                    </Box>
                </Menu>
                <Button id="status-filter-button" onClick={handleStatusMenuOpen} endIcon={<KeyboardArrowDownIcon />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isStatusMenuOpen || statusFilter.length < statusOptions.length ? '#0064FF' : theme.palette.divider, backgroundColor: isStatusMenuOpen || statusFilter.length < statusOptions.length ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' } }}>
                    <Typography component="span" sx={{ fontSize: '14px' }}>{getStatusButtonText()}</Typography>
                </Button>
                <Menu anchorEl={statusMenuAnchorEl} open={isStatusMenuOpen} onClose={handleStatusMenuClose} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}>
                    <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontSize: '14px' }}>Select Status</Typography></Box>
                    <Divider />
                    <Box sx={{ py: '8px' }}>
                        <FormControlLabel label="All" sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempStatusFilter.length === statusOptions.length && { backgroundColor: '#F3F8FE' }) }} control={<Checkbox size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} checked={tempStatusFilter.length === statusOptions.length} indeterminate={tempStatusFilter.length > 0 && tempStatusFilter.length < statusOptions.length} onChange={handleSelectAllStatuses} />} />
                        <Divider sx={{ borderColor: 'white', my: 0.1 }} /> 
                        {statusOptions.map((s, index) => (
                           <React.Fragment key={s}>
                            <FormControlLabel value={s} control={<Checkbox checked={tempStatusFilter.includes(s)} onChange={() => handleTempStatusChange(s)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={s} sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempStatusFilter.includes(s) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempStatusFilter.includes(s) ? '#333333' : '#666666', fontSize: '14px' } } }}/>
                            {index < providerOptions.length - 1 && <Divider sx={{ borderColor: 'white', my: 0.1 }} />}
                           </React.Fragment>
                        ))}
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '2px' }}>
                        <Button onClick={handleClearStatus} sx={{ textTransform: 'none', fontSize:'14px', color: tempStatusFilter.length > 0 ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button>
                        <Button onClick={handleApplyStatus} sx={{ textTransform: 'none', fontSize: '14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button>
                    </Box>
                </Menu>
            </Paper>

            <Paper elevation={0} sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: '8px', overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <DataErrorState onRefresh={refetch} />
                ) : budgets.length === 0 ? (
                    <BudgetEmptyState />
                ) : (
                    <TableContainer sx={{ overflow: 'auto' }}>
                        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', paddingTop: '30px', paddingLeft: '18px', paddingRight: '18px' } }}>
                                    <TableCell sx={{ width: '16%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Budget Name/ Description</TableCell>
                                    <TableCell sx={{ width: '10%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Budget Period</TableCell>
                                    <TableCell sx={{ width: '14%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Status</TableCell>
                                    <TableCell sx={{ width: '15%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Spend/ Budget</TableCell>
                                    <TableCell sx={{ width: '9%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Forecasted</TableCell>
                                    <TableCell sx={{ width: '12%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Used Credits <Tooltip title="Indicates the amount of promotional credits consumed within the budget period. You can choose whether these credits count towards the budget spend." placement="top" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#333333', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }}><InfoOutlinedIcon sx={{ fontSize: '16px', color: '#0064FF', cursor: 'pointer' }} /></Tooltip></Box>
                                    </TableCell>
                                    <TableCell sx={{ width: '17%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Added By</TableCell>
                                    <TableCell sx={{ width: '5%' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map((row: Budget, index: number) => {
                                    const statusInfo = mapApiStatusToDisplay(row);
                                    const spendPercentage = row.budgetLimit > 0 ? (row.spend / row.budgetLimit) * 100 : 0;
                                    const firstScope = row.scopeConfigurations?.[0];
                                    return (
                                        <TableRow key={row.id} onClick={() => handleRowClick(row)} onMouseEnter={() => setHoveredRowId(row.id)} onMouseLeave={() => setHoveredRowId(null)} sx={{ cursor: 'pointer', '& td, & th': { border: 0, padding: '18px', height: '75px', verticalAlign: 'top' }, backgroundColor: isPanelOpen && selectedBudgetForPanel?.id === row.id ? 'rgba(242, 247, 255, 1)' : (index % 2 !== 0 ? 'transparent' : '#F9FAFB'), '&:hover': { backgroundColor: 'rgba(242, 247, 255, 1)' } }}>
                                            <TableCell component="th" scope="row">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>{row.name}</Typography>
                                                    <Tooltip title={ (() => { const chipSx = { fontSize: '10px', fontWeight: 400, lineHeight: '15px', color: '#333333', backgroundColor: '#F0F0F0', height: '22px', border: '1px solid #CCCCCC' }; return ( <Box sx={{ p: 0, color: '#333', fontFamily: 'Inter' }}> <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333333', mb: 1.5 }}>Budget Applies To</Typography> <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#666666', mb: 0.5 }}>Data Source</Typography> <Chip size="small" label={firstScope?.dataSources?.map(ds => ds.name).join(', ') || 'N/A'} sx={{ ...chipSx, mb: 1.5 }} /> <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#666666', mb: 0.5 }}>Region</Typography> <Chip size="small" label={firstScope?.regions?.join(', ') || 'Any'} sx={{ ...chipSx, mb: 1.5 }} /> <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#666666', mb: 1 }}>Services</Typography> <Chip size="small" label={firstScope?.services?.join(', ') || 'Any'} sx={{ ...chipSx, mb: 1.5 }} /> </Box> ); })() } placement="bottom-start" arrow componentsProps={{ tooltip: { sx: { width: 289, maxWidth: 'none', backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px #6E6E6E3D', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }} >
                                                        <IconButton size="small" sx={{ p: 0 }} aria-label="info" onClick={(e) => e.stopPropagation()}><img src="/icons/info-icon-blue.svg" alt="info" style={{ width: '14px', height: '14px' }} /></IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Typography variant="body2" noWrap sx={{ fontSize: '12px', fontWeight: 400, color: '#666666', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', fontWeight: 400, color: '#333333', textTransform: 'capitalize' }}>{row.period.toLowerCase()}</TableCell>
                                            <TableCell><Chip label={statusInfo.status} size="small" sx={getStatusChipSx(statusInfo.status)} /></TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>{formatValue(row.spend, row.metric)} / {formatValue(row.budgetLimit, row.metric)}</Typography>
                                                <Box sx={{ width: '100%', height: '8px', borderRadius: '4px', backgroundColor: '#EAEAEA', mt: 1, position: 'relative', overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', width: `${Math.min(100, spendPercentage)}%`, backgroundColor: statusInfo.color, borderRadius: '4px' }} />
                                                    {row.thresholds && row.thresholds.map(threshold => ( (threshold > 0 && threshold < 100) && <Box key={threshold} sx={{ position: 'absolute', left: `calc(${threshold}% - 1px)`, top: 0, width: '2px', height: '100%', backgroundColor: 'white', zIndex: 2 }}/> ))}
                                                </Box>
                                                {statusInfo.message && ( <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, whiteSpace: 'nowrap' }}> <InfoOutlinedIcon sx={{ color: statusInfo.color, fontSize: '14px', mr: 0.5 }} /> <Typography sx={{ fontSize: '12px', color: '#666666' }}>{statusInfo.message}</Typography> </Box> )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', fontWeight: 400, color: '#333333' }}>{formatValue(row.forecasted, row.metric)}</TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#333333' }}>
                                                    {row.credits > 0 ? formatValue(row.credits, row.metric) : '-'}
                                                </Typography>
                                                {row.credits > 0 && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                        {row.includeCredits ? ( <CheckCircleIcon sx={{ color: '#03A65A', fontSize: '14px', mr: 0.5 }} /> ) : ( <WarningIcon sx={{ color: '#D4A72C', fontSize: '14px', mr: 0.5 }} /> )}
                                                        <Typography sx={{ fontSize: '12px', color: row.includeCredits ? '#03A65A' : '#D4A72C' }}>
                                                            {row.includeCredits ? 'Included' : 'Not Included'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                                    <Avatar alt={row.addedBy.name || ''} src={row.addedBy.photo || undefined} sx={{ width: '18px', height: '18px', fontSize: '12px' }} >{!row.addedBy.photo && row.addedBy.name?.charAt(0).toUpperCase()}</Avatar>
                                                    <Typography noWrap component="span" sx={{ fontSize: '14px', color: '#333333' }}>{row.addedBy.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {userRole !== 'viewer' && (
                                                    <IconButton aria-label="actions" onClick={(e) => handleMenuOpen(e, row)} sx={{ visibility: hoveredRowId === row.id || (isActionMenuOpen && selectedBudget?.id === row.id) ? 'visible' : 'hidden', width: '32px', height: '32px', backgroundColor: '#FFFFFF', border: ' rgb(203, 203, 203) solid 1px', borderRadius: '8px', '&:hover': { backgroundColor: '#F5F5F5' } }}>
                                                        <MoreHorizIcon sx={{ width: '18px', height: 'auto', color: '#333333' }} />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            <BudgetDetailsPanel open={isPanelOpen} onClose={handlePanelClose} budget={selectedBudgetForPanel} />
            <Menu anchorEl={actionMenuAnchorEl} open={isActionMenuOpen} onClose={handleMenuClose} PaperProps={{ sx: { width: '119px', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, '& .MuiList-root': { padding: '12px 0' }, marginLeft:"-12px" } }} >
                {userRole !== 'viewer' && <MenuItem onClick={handleEdit} sx={{ py: 1, px: 2 }}> <ListItemIcon sx={{ minWidth: 'auto', marginRight: '-7px' }}><img src="/icons/edit_icon.svg" alt="Edit" style={{ width: '12px', height: '12px' }} /></ListItemIcon><ListItemText primary="Edit" sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 400 } }} /> </MenuItem>}
                {userRole !== 'viewer' && <MenuItem onClick={handleDelete} sx={{ py: 1, px: 2 }} disabled={deleteLoading}> <ListItemIcon sx={{ minWidth: 'auto', marginRight: '-7px' }}><img src="/icons/delete_icon.svg" alt="Delete" style={{ width: '12px', height: '12px' }} /></ListItemIcon><ListItemText primary="Delete" sx={{ '& .MuiTypography-root': { fontSize: '14px', color: '#CA2B20', fontWeight: 400 } }} /> {deleteLoading && <CircularProgress size={12} sx={{ ml: 1 }} />} </MenuItem>}
            </Menu>
        </Box>
    );
};

export default TotalBudgetsPage;



// import React, { useState, useMemo, useEffect } from 'react';
// import {
//     Box, Typography, Button, TextField, InputAdornment, Paper, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
//     MenuItem, useTheme, Divider, Menu, Checkbox, FormControlLabel, IconButton,
//     Chip, ListItemIcon, ListItemText, Tooltip, CircularProgress, FormGroup
// } from '@mui/material';
// import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
// import { gql, useMutation } from '@apollo/client';
// import {
//     FiberManualRecord as FiberManualRecordIcon,
//     KeyboardArrowDown as KeyboardArrowDownIcon,
//     MoreHoriz as MoreHorizIcon,
//     InfoOutlined as InfoOutlinedIcon,
//     CheckCircle as CheckCircleIcon,
//     Warning as WarningIcon,
// } from '@mui/icons-material';
// import SuccessToast from '../../SuccessToast';
// import BudgetDetailsPanel from './BudgetDetailsPanel';
// import BudgetEmptyState from './BudgetEmptyState';

// // --- GraphQL Mutations ---
// const DELETE_BUDGET = gql`
//     mutation DeleteBudget($id: ID!) {
//         deleteBudget(id: $id)
//     }
// `;

// // --- Helper functions ---
// const formatCurrency = (value: number) => {
//     if (typeof value !== 'number') return value;
//     return new Intl.NumberFormat('en-US', {
//         style: 'currency',
//         currency: 'USD',
//         minimumFractionDigits: 0,
//         maximumFractionDigits: 0,
//     }).format(value);
// };

// const getProviderIcon = (providerType: string): string => {
//   switch (providerType) {
//     case 'GCP': return '/icons/google-cloud-icon-2048x1646-7admxejz.svg';
//     case 'AWS': return '/icons/aws.svg';
//     case 'Azure': return '/icons/azure2.svg';
//     default: return '';
//   }
// };

// // --- Interfaces ---
// interface Creator { id?: string; name: string | null; photo?: string | null; }
// interface ScopeConfiguration {
//     id: string;
//     dataSources: { id: string, name: string }[];
//     regions: string[] | null;
//     services: string[] | null;
//     labels: { [key: string]: string; } | null;
//     k8sClusters: string[] | null;
//     k8sNamespaces: string[] | null;
// }
// interface Budget {
//     id: string;
//     name: string;
//     description: string;
//     provider: string;
//     period: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
//     status: 'ON_TRACK' | 'THRESHOLD_BREACHED' | 'BUDGET_BREACHED';
//     spend: number;
//     budgetLimit: number;
//     forecasted: number;
//     credits: number;
//     addedBy: Creator;
//     thresholds: number[];
//     includeCredits: boolean;
//     tags?: { [key: string]: string; } | null | undefined;
//     scopeConfigurations: ScopeConfiguration[];
//     userIdsToNotify: string[];
// }
// interface Tenant { id: string; name: string; }
// interface User { id: string; name?: string | null; photo?: string | null; }

// interface BudgetContextType {
//   currentTenant: Tenant | null;
//   userRole?: string | null;
//   setSidebarOpen?: (isOpen: boolean) => void;
//   data: { budgetsByTenant: any[] };
//   loading: boolean;
//   error: any;
//   refetch: () => void;
// }

// // --- Helper function to get specific threshold message ---
// const getBreachedThresholdMessage = (spend: number, budgetLimit: number, thresholds: number[]): string => {
//     if (budgetLimit <= 0 || !thresholds || thresholds.length === 0) {
//         return 'A spending threshold has been breached';
//     }
//     const percentage = (spend / budgetLimit) * 100;
//     const sortedThresholds = [...thresholds].sort((a, b) => b - a);
//     for (const threshold of sortedThresholds) {
//         if (percentage >= threshold) {
//             return `${threshold}% threshold breached`;
//         }
//     }
//     return 'A spending threshold has been breached';
// };

// // --- Main status mapping function now generates specific messages ---
// const mapApiStatusToDisplay = (budget: Budget) => {
//     const { status, spend, budgetLimit, thresholds } = budget;
//     switch (status) {
//         case 'ON_TRACK':
//             return { status: 'On Track', color: '#03A65A', message: '' };
//         case 'THRESHOLD_BREACHED':
//              return {
//                  status: 'Threshold Breached',
//                  color: '#D4A72C',
//                  message: getBreachedThresholdMessage(spend, budgetLimit, thresholds)
//              };
//         case 'BUDGET_BREACHED':
//             return { status: 'Budget Breached', color: '#CA2B20', message: 'Spend exceeds budget limit' };
//         default:
//             return { status: 'On Track', color: '#03A65A', message: '' };
//     }
// };

// const getStatusChipSx = (status: string): any => {
//     switch (status) {
//         case 'On Track': return { backgroundColor: 'rgba(3, 166, 90, 0.1)', color: 'rgba(3, 166, 90, 1)', fontWeight: 500, pointerEvents: 'none', border: '1px solid rgba(3, 166, 90, 1)' };
//         case 'Threshold Breached': return { backgroundColor: 'rgba(255, 193, 7, 0.1)', color: 'rgba(212, 167, 44, 1)', fontWeight: 500, pointerEvents: 'none', border: '1px solid rgba(212, 167, 44, 1)' };
//         case 'Budget Breached': return { backgroundColor: 'rgb(240, 213, 211)', color: 'rgba(202, 43, 32, 1)', fontWeight: 500, pointerEvents: 'none', border: '1px solid rgba(202, 43, 32, 1)' };
//         default: return { backgroundColor: 'grey.200', color: 'grey.800', fontWeight: 500, border: '1px solid grey.400' };
//     }
// };

// const providerOptions = ['GCP', 'AWS', 'Azure'];
// const periodOptions = ['Monthly', 'Quarterly', 'Yearly'];
// const statusOptions = ['On Track', 'Threshold Breached', 'Budget Breached'];

// // --- 1. Define the Error State Component in the same file ---
// interface DataErrorStateProps {
//     onRefresh: () => void;
// }

// const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
//     return (
//         <Box
//             sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 textAlign: 'center',
//                 height: '100%',
//                 minHeight: '400px', 
//                 p: 3,
//                 gap: 1,
//             }}
//         >
//             <img 
//                 src="/icons/unable_to_diplay_data.svg" 
//                 alt="Hourglass indicating a temporary issue" 
//                 style={{ width: '120px', height: '120px' }} 
//             />
//             <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
//                 Unable to Display Data
//             </Typography>
//             <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
//                 We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
//             </Typography>
//             <Button
//                 variant="text"
//                 onClick={onRefresh}
//                 sx={{
//                     color: '#0064FF',
//                     textTransform: 'none',
//                     fontWeight: 500,
//                     fontSize: '14px',
//                     marginTop: '2px',
//                     '&:hover': {
//                         backgroundColor: 'rgba(0, 100, 255, 0.04)'
//                     }
//                 }}
//             >
//                 Refresh Page
//             </Button>
//         </Box>
//     );
// };


// // --- Main Page Component ---
// const TotalBudgetsPage: React.FC = () => {
//     const theme = useTheme();
//     const navigate = useNavigate();
//     const location = useLocation();
//     const { 
//         currentTenant, 
//         userRole, 
//         setSidebarOpen, 
//         data, 
//         loading, 
//         error, 
//         refetch 
//     } = useOutletContext<BudgetContextType>();

//     const [deleteBudget, { loading: deleteLoading }] = useMutation(DELETE_BUDGET);
//     const [isPanelOpen, setIsPanelOpen] = useState(false);
//     const [selectedBudgetForPanel, setSelectedBudgetForPanel] = useState<Budget | null>(null);
//     const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
//     const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
//     const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
//     const isActionMenuOpen = Boolean(actionMenuAnchorEl);
//     const [toastOpen, setToastOpen] = useState(false);
//     const [toastMessage, setToastMessage] = useState('');
//     const [searchTerm, setSearchTerm] = useState('');
//     const [providerFilters, setProviderFilters] = useState<string[]>(providerOptions);
//     const [tempProviderFilters, setTempProviderFilters] = useState<string[]>(providerOptions);
//     const [providerMenuAnchorEl, setProviderMenuAnchorEl] = useState<null | HTMLElement>(null);
//     const isProviderMenuOpen = Boolean(providerMenuAnchorEl);
//     const [periodFilter, setPeriodFilter] = useState<string[]>(periodOptions);
//     const [tempPeriodFilter, setTempPeriodFilter] = useState<string[]>(periodOptions);
//     const [periodMenuAnchorEl, setPeriodMenuAnchorEl] = useState<null | HTMLElement>(null);
//     const isPeriodMenuOpen = Boolean(periodMenuAnchorEl);
//     const [statusFilter, setStatusFilter] = useState<string[]>(statusOptions);
//     const [tempStatusFilter, setTempStatusFilter] = useState<string[]>(statusOptions);
//     const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState<null | HTMLElement>(null);
//     const isStatusMenuOpen = Boolean(statusMenuAnchorEl);


//     const budgets: Budget[] = useMemo(() => {
//         if (!data?.budgetsByTenant) return [];
//         return data.budgetsByTenant.map((b: any): Budget => ({
//             id: b.id,
//             name: b.name,
//             description: b.description || '',
//             provider: b.provider,
//             period: b.period,
//             status: b.status,
//             spend: b.currentSpend,
//             budgetLimit: b.amount,
//             forecasted: b.forecastedSpend,
//             credits: b.currentCredits,
//             addedBy: { name: b.creator?.name || 'N/A', photo: b.creator?.photo || undefined },
//             thresholds: b.alertThresholds,
//             includeCredits: b.includeCredits,
//             tags: b.tags,
//             userIdsToNotify: b.userIdsToNotify || [],
//             scopeConfigurations: b.scopeConfigurations.map((sc: any) => ({
//                 id: sc.id,
//                 dataSources: sc.dataSources,
//                 regions: sc.regions,
//                 services: sc.services,
//                 labels: sc.labels
//                     ? sc.labels.reduce((acc: { [key: string]: string }, label: { key: string; value: string }) => {
//                           acc[label.key] = label.value;
//                           return acc;
//                       }, {})
//                     : null,
//                 k8sClusters: sc.k8sClusters,
//                 k8sNamespaces: sc.k8sNamespaces,
//             })),
//         }));
//     }, [data]);

//      useEffect(() => {
//         const state = location.state as { budgetAdded?: boolean; budgetUpdated?: boolean; budgetName?: string; };
//         if (state?.budgetAdded) { setToastMessage(`Budget "${state.budgetName || 'new budget'}" added successfully`); setToastOpen(true); refetch(); navigate(location.pathname, { replace: true, state: {} }); }
//         else if (state?.budgetUpdated) { setToastMessage(`Budget "${state.budgetName || 'budget'}" updated successfully`); setToastOpen(true); refetch(); navigate(location.pathname, { replace: true, state: {} }); }
//     }, [location, navigate, refetch]);
//     const onTrackCount = useMemo(() => budgets.filter(b => b.status === 'ON_TRACK').length, [budgets]);
//     const thresholdBreachedCount = useMemo(() => budgets.filter(b => b.status === 'THRESHOLD_BREACHED').length, [budgets]);
//     const budgetBreachedCount = useMemo(() => budgets.filter(b => b.status === 'BUDGET_BREACHED').length, [budgets]);
//     const filteredData = useMemo(() => {
//         return budgets.filter(budget => {
//             const displayStatus = mapApiStatusToDisplay(budget).status;
//             const matchesSearch = budget.name.toLowerCase().includes(searchTerm.toLowerCase());
//             const matchesProvider = providerFilters.length === providerOptions.length || providerFilters.includes(budget.provider);
//             const matchesPeriod = periodFilter.length === periodOptions.length || periodFilter.map(p => p.toUpperCase()).includes(budget.period);
//             const matchesStatus = statusFilter.length === statusOptions.length || statusFilter.includes(displayStatus);
//             return matchesSearch && matchesProvider && matchesPeriod && matchesStatus;
//         });
//     }, [budgets, searchTerm, providerFilters, periodFilter, statusFilter]);
//     const handleRowClick = (budget: Budget) => { if (typeof setSidebarOpen === 'function') { setSidebarOpen(false); } setSelectedBudgetForPanel(budget); setIsPanelOpen(true); };
//     const handlePanelClose = () => { if (typeof setSidebarOpen === 'function') { setSidebarOpen(true); } setIsPanelOpen(false); setSelectedBudgetForPanel(null); };
//     const handleAddBudgetClick = () => navigate('/budget-alerts/add', { state: { tenantId: currentTenant?.id } });
//     const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, budget: Budget) => { event.stopPropagation(); setActionMenuAnchorEl(event.currentTarget); setSelectedBudget(budget); };
//     const handleMenuClose = () => { setActionMenuAnchorEl(null); setSelectedBudget(null); };
//     const handleEdit = () => {
//         const budgetToEdit = selectedBudget;
//         handleMenuClose();
//         if (budgetToEdit && budgetToEdit.id) {
//             navigate(`/budget-alerts/edit/${budgetToEdit.id}`);
//         } else {
//             console.error("Edit failed: Budget data is incomplete or ID is missing.", budgetToEdit);
//             setToastMessage("Could not edit budget. The budget ID is missing.");
//             setToastOpen(true);
//         }
//     };
    
//     // const handleDelete = async () => {
//     //     const budgetToDelete = selectedBudget;
//     //     handleMenuClose();
//     //     if (!budgetToDelete) return;
//     //     try {
//     //         await deleteBudget({ variables: { id: budgetToDelete.id } });
//     //         setToastMessage(`Budget "${budgetToDelete.name}" deleted successfully.`);
//     //         setToastOpen(true);
//     //         refetch();
//     //     }
//     //     catch (err: any) {
//     //         console.error("Error deleting budget:", err);
//     //         setToastMessage(`Error: ${err.message}`);
//     //         setToastOpen(true);
//     //     }
//     // };

//     const handleDelete = async () => {
//     const budgetToDelete = selectedBudget;
//     handleMenuClose();

//     if (!budgetToDelete) {
//       return; 
//     }

//     try {
//         await deleteBudget({ variables: { id: budgetToDelete.id } });
//         setToastMessage(`Budget "${budgetToDelete.name}" deleted successfully.`);
//         setToastOpen(true);
//         refetch();
//     }
//     catch (err: any) {
//         // Log the technical error for developers
//         console.error("Error deleting budget:", err);
        
//         // Show the user-friendly message
//         setToastMessage("We are unable to delete the budget at this moment. Please try again after some time.");
//         setToastOpen(true);
//     }
// };

//     const handleProviderMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
//       setTempProviderFilters(providerFilters);
//       setProviderMenuAnchorEl(event.currentTarget);
//     };
//     const handleProviderMenuClose = () => {
//       setProviderMenuAnchorEl(null);
//     };
//     const handleApplyProvider = () => {
//       setProviderFilters(tempProviderFilters);
//       handleProviderMenuClose();
//     };
//     const handleClearProvider = () => {
//       setTempProviderFilters([]);
//       setProviderFilters([]);
//       handleProviderMenuClose();
//     };
//     const handleProviderCheckboxChange = (provider: string) => {
//       setTempProviderFilters(prev =>
//           prev.includes(provider)
//               ? prev.filter(p => p !== provider)
//               : [...prev, provider]
//       );
//     };
//     const handleSelectAllProviders = (event: React.ChangeEvent<HTMLInputElement>) => {
//         setTempProviderFilters(event.target.checked ? providerOptions : []);
//     };
//     const getProviderButtonText = () => {
//       const count = providerFilters.length;
//       if (count === providerOptions.length) return 'Cloud Provider';
//       return `${count} Providers`;
//     };
//     const handlePeriodMenuOpen = (e: React.MouseEvent<HTMLElement>) => { setTempPeriodFilter([...periodFilter]); setPeriodMenuAnchorEl(e.currentTarget); };
//     const handlePeriodMenuClose = () => setPeriodMenuAnchorEl(null);
//     const handleApplyPeriod = () => { setPeriodFilter(tempPeriodFilter); handlePeriodMenuClose(); };
//     const handleTempPeriodChange = (period: string) => { const newSelection = tempPeriodFilter.includes(period) ? tempPeriodFilter.filter(p => p !== period) : [...tempPeriodFilter, period]; setTempPeriodFilter(newSelection); };
//     const handleSelectAllPeriods = (event: React.ChangeEvent<HTMLInputElement>) => { setTempPeriodFilter(event.target.checked ? periodOptions : []); };
//     const handleClearPeriod = () => {
//         setTempPeriodFilter([]);
//         setPeriodFilter([]);
//         handlePeriodMenuClose();
//     };
//     const getPeriodButtonText = () => {
//         const count = periodFilter.length;
//         if (count === periodOptions.length) return 'Period';
//         return `${count} Periods`;
//     };
//     const handleStatusMenuOpen = (e: React.MouseEvent<HTMLElement>) => { setTempStatusFilter([...statusFilter]); setStatusMenuAnchorEl(e.currentTarget); };
//     const handleStatusMenuClose = () => setStatusMenuAnchorEl(null);
//     const handleApplyStatus = () => { setStatusFilter(tempStatusFilter); handleStatusMenuClose(); };
//     const handleTempStatusChange = (status: string) => { const newSelection = tempStatusFilter.includes(status) ? tempStatusFilter.filter(s => s !== status) : [...tempStatusFilter, status]; setTempStatusFilter(newSelection); };
//     const handleSelectAllStatuses = (event: React.ChangeEvent<HTMLInputElement>) => { setTempStatusFilter(event.target.checked ? statusOptions : []); };
//     const handleClearStatus = () => {
//         setTempStatusFilter([]);
//         setStatusFilter([]);
//         handleStatusMenuClose();
//     };
//     const getStatusButtonText = () => {
//         const count = statusFilter.length;
//         if (count === statusOptions.length) return 'Status';
//         return `${count} Statuses`;
//     };
        
//     return (
//         <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', boxSizing: 'border-box' }}>
//             <SuccessToast open={toastOpen} message={toastMessage} onClose={() => setToastOpen(false)} />
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', flexShrink: 0 }}>
//                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
//                      <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 600, mb: -1 }}>Total Budgets ({!loading && !error ? budgets.length : 0})</Typography>
//                      <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 0, mt: -0.4 }}> Prevent overspending by reviewing and editing your cloud budgets at a glance. </Typography>
//                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
//                          <Chip icon={<FiberManualRecordIcon sx={{ fill: theme.palette.success.main, width: '10px', height: '10px' }} />} label={`On Track ${onTrackCount}`} size="small" sx={{ gap: '4px', backgroundColor: 'rgba(232, 245, 233, 1)', color: 'rgba(102, 102, 102, 1)', fontWeight: 400, borderRadius: '14px', padding: '0px 8px', pointerEvents: 'none' }} />
//                          <Chip icon={<FiberManualRecordIcon sx={{ fill: 'rgba(212, 167, 44, 1)', width: '10px', height: '10px' }} />} label={`Threshold Breached ${thresholdBreachedCount}`} size="small" sx={{ gap: '4px', backgroundColor: 'rgba(255, 193, 7, 0.1)', color: 'rgba(102, 102, 102, 1)', fontWeight: 400, borderRadius: '14px', padding: '0px 8px', pointerEvents: 'none' }} />
//                          <Chip icon={<FiberManualRecordIcon sx={{ fill: '#CA2B20', width: '10px', height: '10px' }} />} label={`Budget Breached ${budgetBreachedCount}`} size="small" sx={{ gap: '4px', backgroundColor: 'rgba(202, 43, 32, 0.1)', color: 'rgba(102, 102, 102, 1)', fontWeight: 400, borderRadius: '14px', padding: '0px 8px', pointerEvents: 'none' }} />
//                      </Box>
//                  </Box>
//                  {userRole !== 'viewer' && (
//                      <Button onClick={handleAddBudgetClick} sx={{ backgroundColor: '#0064FF', width: '145px', height: '36px', textTransform: 'none', fontSize: '14px', fontWeight: 500, borderRadius: '8px', color: ' #FFFFFF' }}>Add New Budget</Button>
//                  )}
//             </Box>

//             <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', flexShrink: 0 }}>
//                  <TextField variant="outlined" size="small" placeholder="Search by budget name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ minWidth: { xs: '10px', sm: '250px' }, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'var(--black-4, rgba(0, 0, 0, 0.04))', color: '#999999', fontWeight: '300', '& fieldset': { border: 0 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight: 300, fontSize: '14px', color: '#999999' } }}
//                  InputProps={{ startAdornment: (<InputAdornment position="start"> <img src="/icons/search_icon.svg" alt="Search" style={{ width: '17px', height: '17px' }} /> </InputAdornment>) }} />
//                 <Button
//                     id="provider-filter-button" onClick={handleProviderMenuOpen} endIcon={<KeyboardArrowDownIcon />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isProviderMenuOpen || providerFilters.length < providerOptions.length ? '#0064FF' : theme.palette.divider, backgroundColor: isProviderMenuOpen || providerFilters.length < providerOptions.length ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }, }}>
//                    <Typography component="span" sx={{ fontSize: '14px' }}>{getProviderButtonText()}</Typography>
//                 </Button>
//                 <Menu
//                     id="provider-filter-menu" anchorEl={providerMenuAnchorEl} open={isProviderMenuOpen} onClose={handleProviderMenuClose} MenuListProps={{ 'aria-labelledby': 'provider-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}>
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Provider</Typography></Box>
//                     <Divider />
//                     <Box sx={{ py: '10px' }}>
//                         <FormControlLabel label="All" sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempProviderFilters.length === providerOptions.length && { backgroundColor: '#F3F8FE' }) }} control={ <Checkbox size="small" checked={tempProviderFilters.length === providerOptions.length} indeterminate={tempProviderFilters.length > 0 && tempProviderFilters.length < providerOptions.length} onChange={handleSelectAllProviders} sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } />
//                         <Divider sx={{ borderColor: 'white', my: 0.1 }} />                        
//                         <FormGroup>
//                             {providerOptions.map((provider, index) => (
//                                 <React.Fragment key={provider}>
//                                     <FormControlLabel control={ <Checkbox checked={tempProviderFilters.includes(provider)} onChange={() => handleProviderCheckboxChange(provider)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><img src={getProviderIcon(provider)} alt={provider} style={{ height: 20, width: 20, marginRight: 8 }} />{provider}</Box> } sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempProviderFilters.includes(provider) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempProviderFilters.includes(provider) ? '#333333' : '#666666', fontSize: '14px' } } }} />
//                                     {index < providerOptions.length - 1 && <Divider sx={{ borderColor: 'white', my: 0.1 }} />}
//                                 </React.Fragment>
//                             ))}
//                         </FormGroup>
//                     </Box>
//                     <Divider />
//                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}>
//                         <Button onClick={handleClearProvider} sx={{ textTransform: 'none', fontSize:'14px', color: tempProviderFilters.length > 0 ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button>
//                         <Button onClick={handleApplyProvider} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button>
//                     </Box>
//                 </Menu>
//                 <Button id="period-filter-button" onClick={handlePeriodMenuOpen} endIcon={<KeyboardArrowDownIcon />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isPeriodMenuOpen || periodFilter.length < periodOptions.length ? '#0064FF' : theme.palette.divider, backgroundColor: isPeriodMenuOpen || periodFilter.length < periodOptions.length ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' } }}>
//                     <Typography component="span" sx={{ fontSize: '14px' }}>{getPeriodButtonText()}</Typography>
//                 </Button>
//                 <Menu anchorEl={periodMenuAnchorEl} open={isPeriodMenuOpen} onClose={handlePeriodMenuClose} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}>
//                      <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontSize: '14px' }}>Select Period</Typography></Box>
//                     <Divider />
//                     <Box sx={{ py: '8px' }}>
//                         <FormControlLabel label="All" sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempPeriodFilter.length === periodOptions.length && { backgroundColor: '#F3F8FE' }) }} control={<Checkbox size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} checked={tempPeriodFilter.length === periodOptions.length} indeterminate={tempPeriodFilter.length > 0 && tempPeriodFilter.length < periodOptions.length} onChange={handleSelectAllPeriods} />} />
//                         <Divider sx={{ borderColor: 'white', my: 0.1 }} /> 
//                         {periodOptions.map((p, index) => (
//                            <React.Fragment key={p}>
//                                 <FormControlLabel value={p} control={<Checkbox checked={tempPeriodFilter.includes(p)} onChange={() => handleTempPeriodChange(p)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={p} sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempPeriodFilter.includes(p) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempPeriodFilter.includes(p) ? '#333333' : '#666666', fontSize: '14px' } } }}/>
//                                 {index < providerOptions.length - 1 && <Divider sx={{ borderColor: 'white', my: 0.1 }} />}
//                            </React.Fragment>
//                         ))}
//                     </Box>
//                     <Divider />
//                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '2px' }}>
//                         <Button onClick={handleClearPeriod} sx={{ textTransform: 'none', fontSize:'14px', color: tempPeriodFilter.length > 0 ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button>
//                         <Button onClick={handleApplyPeriod} sx={{ textTransform: 'none', fontSize: '14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button>
//                     </Box>
//                 </Menu>
//                 <Button id="status-filter-button" onClick={handleStatusMenuOpen} endIcon={<KeyboardArrowDownIcon />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isStatusMenuOpen || statusFilter.length < statusOptions.length ? '#0064FF' : theme.palette.divider, backgroundColor: isStatusMenuOpen || statusFilter.length < statusOptions.length ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' } }}>
//                     <Typography component="span" sx={{ fontSize: '14px' }}>{getStatusButtonText()}</Typography>
//                 </Button>
//                 <Menu anchorEl={statusMenuAnchorEl} open={isStatusMenuOpen} onClose={handleStatusMenuClose} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}>
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontSize: '14px' }}>Select Status</Typography></Box>
//                     <Divider />
//                     <Box sx={{ py: '8px' }}>
//                         <FormControlLabel label="All" sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempStatusFilter.length === statusOptions.length && { backgroundColor: '#F3F8FE' }) }} control={<Checkbox size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} checked={tempStatusFilter.length === statusOptions.length} indeterminate={tempStatusFilter.length > 0 && tempStatusFilter.length < statusOptions.length} onChange={handleSelectAllStatuses} />} />
//                         <Divider sx={{ borderColor: 'white', my: 0.1 }} /> 
//                         {statusOptions.map((s, index) => (
//                            <React.Fragment key={s}>
//                             <FormControlLabel value={s} control={<Checkbox checked={tempStatusFilter.includes(s)} onChange={() => handleTempStatusChange(s)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={s} sx={{ width: '100%', m: 0, px: '16px', py: '4px', ...(tempStatusFilter.includes(s) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempStatusFilter.includes(s) ? '#333333' : '#666666', fontSize: '14px' } } }}/>
//                             {index < providerOptions.length - 1 && <Divider sx={{ borderColor: 'white', my: 0.1 }} />}
//                            </React.Fragment>
//                         ))}
//                     </Box>
//                     <Divider />
//                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '2px' }}>
//                         <Button onClick={handleClearStatus} sx={{ textTransform: 'none', fontSize:'14px', color: tempStatusFilter.length > 0 ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button>
//                         <Button onClick={handleApplyStatus} sx={{ textTransform: 'none', fontSize: '14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button>
//                     </Box>
//                 </Menu>
//             </Paper>

//             <Paper elevation={0} sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: '8px', overflow: 'hidden' }}>
//                 {loading ? (
//                     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
//                         <CircularProgress />
//                     </Box>
//                 ) : error ? (
//                     <DataErrorState onRefresh={refetch} />
//                 ) : budgets.length === 0 ? (
//                     <BudgetEmptyState />
//                 ) : (
//                     <TableContainer sx={{ overflow: 'auto' }}>
//                         <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
//                             <TableHead>
//                                 <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', paddingTop: '30px', paddingLeft: '18px', paddingRight: '18px' } }}>
//                                     <TableCell sx={{ width: '16%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Budget Name/ Description</TableCell>
//                                     <TableCell sx={{ width: '10%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Budget Period</TableCell>
//                                     <TableCell sx={{ width: '14%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Status</TableCell>
//                                     <TableCell sx={{ width: '15%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Spend/ Budget</TableCell>
//                                     <TableCell sx={{ width: '9%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Forecasted</TableCell>
//                                     <TableCell sx={{ width: '12%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>
//                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Used Credits <Tooltip title="Indicates the amount of promotional credits consumed within the budget period. You can choose whether these credits count towards the budget spend." placement="top" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#333333', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }}><InfoOutlinedIcon sx={{ fontSize: '16px', color: '#0064FF', cursor: 'pointer' }} /></Tooltip></Box>
//                                     </TableCell>
//                                     <TableCell sx={{ width: '17%', fontSize: '12px', fontWeight: 500, color: '#666666' }}>Added By</TableCell>
//                                     <TableCell sx={{ width: '5%' }} />
//                                 </TableRow>
//                             </TableHead>
//                             <TableBody>
//                                 {filteredData.map((row: Budget, index: number) => {
//                                     const statusInfo = mapApiStatusToDisplay(row);
//                                     const spendPercentage = row.budgetLimit > 0 ? (row.spend / row.budgetLimit) * 100 : 0;
//                                     const firstScope = row.scopeConfigurations?.[0];
//                                     return (
//                                         <TableRow key={row.id} onClick={() => handleRowClick(row)} onMouseEnter={() => setHoveredRowId(row.id)} onMouseLeave={() => setHoveredRowId(null)} sx={{ cursor: 'pointer', '& td, & th': { border: 0, padding: '18px', height: '75px', verticalAlign: 'top' }, backgroundColor: isPanelOpen && selectedBudgetForPanel?.id === row.id ? 'rgba(242, 247, 255, 1)' : (index % 2 !== 0 ? 'transparent' : '#F9FAFB'), '&:hover': { backgroundColor: 'rgba(242, 247, 255, 1)' } }}>
//                                             <TableCell component="th" scope="row">
//                                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>{row.name}</Typography>
//                                                     <Tooltip title={ (() => { const chipSx = { fontSize: '10px', fontWeight: 400, lineHeight: '15px', color: '#333333', backgroundColor: '#F0F0F0', height: '22px', border: '1px solid #CCCCCC' }; return ( <Box sx={{ p: 0, color: '#333', fontFamily: 'Inter' }}> <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333333', mb: 1.5 }}>Budget Applies To</Typography> <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#666666', mb: 0.5 }}>Data Source</Typography> <Chip size="small" label={firstScope?.dataSources?.map(ds => ds.name).join(', ') || 'N/A'} sx={{ ...chipSx, mb: 1.5 }} /> <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#666666', mb: 0.5 }}>Region</Typography> <Chip size="small" label={firstScope?.regions?.join(', ') || 'Any'} sx={{ ...chipSx, mb: 1.5 }} /> <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#666666', mb: 1 }}>Services</Typography> <Chip size="small" label={firstScope?.services?.join(', ') || 'Any'} sx={{ ...chipSx, mb: 1.5 }} /> </Box> ); })() } placement="bottom-start" arrow componentsProps={{ tooltip: { sx: { width: 289, maxWidth: 'none', backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px #6E6E6E3D', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }} >
//                                                         <IconButton size="small" sx={{ p: 0 }} aria-label="info" onClick={(e) => e.stopPropagation()}><img src="/icons/info-icon-blue.svg" alt="info" style={{ width: '14px', height: '14px' }} /></IconButton>
//                                                     </Tooltip>
//                                                 </Box>
//                                                 <Typography variant="body2" noWrap sx={{ fontSize: '12px', fontWeight: 400, color: '#666666', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</Typography>
//                                             </TableCell>
//                                             <TableCell sx={{ fontSize: '14px', fontWeight: 400, color: '#333333', textTransform: 'capitalize' }}>{row.period.toLowerCase()}</TableCell>
//                                             <TableCell><Chip label={statusInfo.status} size="small" sx={getStatusChipSx(statusInfo.status)} /></TableCell>
//                                             <TableCell>
//                                                 <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>{formatCurrency(row.spend)} / {formatCurrency(row.budgetLimit)}</Typography>
//                                                 <Box sx={{ width: '100%', height: '8px', borderRadius: '4px', backgroundColor: '#EAEAEA', mt: 1, position: 'relative', overflow: 'hidden' }}>
//                                                     <Box sx={{ height: '100%', width: `${Math.min(100, spendPercentage)}%`, backgroundColor: statusInfo.color, borderRadius: '4px' }} />
//                                                     {row.thresholds && row.thresholds.map(threshold => ( (threshold > 0 && threshold < 100) && <Box key={threshold} sx={{ position: 'absolute', left: `calc(${threshold}% - 1px)`, top: 0, width: '2px', height: '100%', backgroundColor: 'white', zIndex: 2 }}/> ))}
//                                                 </Box>
//                                                 {statusInfo.message && ( <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, whiteSpace: 'nowrap' }}> <InfoOutlinedIcon sx={{ color: statusInfo.color, fontSize: '14px', mr: 0.5 }} /> <Typography sx={{ fontSize: '12px', color: '#666666' }}>{statusInfo.message}</Typography> </Box> )}
//                                             </TableCell>
//                                             <TableCell sx={{ fontSize: '14px', fontWeight: 400, color: '#333333' }}>{formatCurrency(row.forecasted)}</TableCell>
//                                             <TableCell>
//                                                 <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#333333' }}>
//                                                     {row.credits > 0 ? formatCurrency(row.credits) : '-'}
//                                                 </Typography>
//                                                 {row.credits > 0 && (
//                                                     <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
//                                                         {row.includeCredits ? ( <CheckCircleIcon sx={{ color: '#03A65A', fontSize: '14px', mr: 0.5 }} /> ) : ( <WarningIcon sx={{ color: '#D4A72C', fontSize: '14px', mr: 0.5 }} /> )}
//                                                         <Typography sx={{ fontSize: '12px', color: row.includeCredits ? '#03A65A' : '#D4A72C' }}>
//                                                             {row.includeCredits ? 'Included' : 'Not Included'}
//                                                         </Typography>
//                                                     </Box>
//                                                 )}
//                                             </TableCell>
//                                             <TableCell>
//                                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
//                                                     <Avatar alt={row.addedBy.name || ''} src={row.addedBy.photo || undefined} sx={{ width: '18px', height: '18px', fontSize: '12px' }} >{!row.addedBy.photo && row.addedBy.name?.charAt(0).toUpperCase()}</Avatar>
//                                                     <Typography noWrap component="span" sx={{ fontSize: '14px', color: '#333333' }}>{row.addedBy.name}</Typography>
//                                                 </Box>
//                                             </TableCell>
//                                             <TableCell>
//                                                 {/* --- MODIFICATION START --- */}
//                                                 {userRole !== 'viewer' && (
//                                                     <IconButton aria-label="actions" onClick={(e) => handleMenuOpen(e, row)} sx={{ visibility: hoveredRowId === row.id || (isActionMenuOpen && selectedBudget?.id === row.id) ? 'visible' : 'hidden', width: '32px', height: '32px', backgroundColor: '#FFFFFF', border: ' rgb(203, 203, 203) solid 1px', borderRadius: '8px', '&:hover': { backgroundColor: '#F5F5F5' } }}>
//                                                         <MoreHorizIcon sx={{ width: '18px', height: 'auto', color: '#333333' }} />
//                                                     </IconButton>
//                                                 )}
//                                                 {/* --- MODIFICATION END --- */}
//                                             </TableCell>
//                                             {/* <TableCell>
//                                                 <IconButton aria-label="actions" onClick={(e) => handleMenuOpen(e, row)} sx={{ visibility: hoveredRowId === row.id || (isActionMenuOpen && selectedBudget?.id === row.id) ? 'visible' : 'hidden', width: '32px', height: '32px', backgroundColor: '#FFFFFF', border: ' rgb(203, 203, 203) solid 1px', borderRadius: '8px', '&:hover': { backgroundColor: '#F5F5F5' } }}>
//                                                     <MoreHorizIcon sx={{ width: '18px', height: 'auto', color: '#333333' }} />
//                                                 </IconButton>
//                                             </TableCell> */}
//                                         </TableRow>
//                                     )
//                                 })}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}
//             </Paper>
//             <BudgetDetailsPanel open={isPanelOpen} onClose={handlePanelClose} budget={selectedBudgetForPanel} />
//             <Menu anchorEl={actionMenuAnchorEl} open={isActionMenuOpen} onClose={handleMenuClose} PaperProps={{ sx: { width: '119px', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, '& .MuiList-root': { padding: '12px 0' }, marginLeft:"-12px" } }} >
//                 {userRole !== 'viewer' && <MenuItem onClick={handleEdit} sx={{ py: 1, px: 2 }}> <ListItemIcon sx={{ minWidth: 'auto', marginRight: '-7px' }}><img src="/icons/edit_icon.svg" alt="Edit" style={{ width: '12px', height: '12px' }} /></ListItemIcon><ListItemText primary="Edit" sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 400 } }} /> </MenuItem>}
//                 {userRole !== 'viewer' && <MenuItem onClick={handleDelete} sx={{ py: 1, px: 2 }} disabled={deleteLoading}> <ListItemIcon sx={{ minWidth: 'auto', marginRight: '-7px' }}><img src="/icons/delete_icon.svg" alt="Delete" style={{ width: '12px', height: '12px' }} /></ListItemIcon><ListItemText primary="Delete" sx={{ '& .MuiTypography-root': { fontSize: '14px', color: '#CA2B20', fontWeight: 400 } }} /> {deleteLoading && <CircularProgress size={12} sx={{ ml: 1 }} />} </MenuItem>}
//             </Menu>
//         </Box>
//     );
// };

// export default TotalBudgetsPage;
